import _defineProperty from "/home/ubuntu/code/addictive-builder/node_modules/@babel/runtime/helpers/esm/defineProperty.js";

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { getCurrentHub } from '@sentry/core';
import { isEvent, extractExceptionKeysForMessage, normalizeToSize, addExceptionMechanism, resolvedSyncPromise, isErrorEvent, isDOMError, isDOMException, addExceptionTypeValue, isError, isPlainObject } from '@sentry/utils';
/**
 * This function creates an exception from a JavaScript Error
 */

function exceptionFromError(stackParser, ex) {
  // Get the frames first since Opera can lose the stack if we touch anything else first
  var frames = parseStackFrames(stackParser, ex);
  var exception = {
    type: ex && ex.name,
    value: extractMessage(ex)
  };

  if (frames.length) {
    exception.stacktrace = {
      frames: frames
    };
  }

  if (exception.type === undefined && exception.value === '') {
    exception.value = 'Unrecoverable error caught';
  }

  return exception;
}
/**
 * @hidden
 */


function eventFromPlainObject(stackParser, exception, syntheticException, isUnhandledRejection) {
  var hub = getCurrentHub();
  var client = hub.getClient();
  var normalizeDepth = client && client.getOptions().normalizeDepth;
  var event = {
    exception: {
      values: [{
        type: isEvent(exception) ? exception.constructor.name : isUnhandledRejection ? 'UnhandledRejection' : 'Error',
        value: "Non-Error ".concat(isUnhandledRejection ? 'promise rejection' : 'exception', " captured with keys: ").concat(extractExceptionKeysForMessage(exception))
      }]
    },
    extra: {
      __serialized__: normalizeToSize(exception, normalizeDepth)
    }
  };

  if (syntheticException) {
    var frames = parseStackFrames(stackParser, syntheticException);

    if (frames.length) {
      // event.exception.values[0] has been set above
      event.exception.values[0].stacktrace = {
        frames: frames
      };
    }
  }

  return event;
}
/**
 * @hidden
 */


function eventFromError(stackParser, ex) {
  return {
    exception: {
      values: [exceptionFromError(stackParser, ex)]
    }
  };
}
/** Parses stack frames from an error */


function parseStackFrames(stackParser, ex) {
  // Access and store the stacktrace property before doing ANYTHING
  // else to it because Opera is not very good at providing it
  // reliably in other circumstances.
  var stacktrace = ex.stacktrace || ex.stack || '';
  var popSize = getPopSize(ex);

  try {
    return stackParser(stacktrace, popSize);
  } catch (e) {// no-empty
  }

  return [];
} // Based on our own mapping pattern - https://github.com/getsentry/sentry/blob/9f08305e09866c8bd6d0c24f5b0aabdd7dd6c59c/src/sentry/lang/javascript/errormapping.py#L83-L108


var reactMinifiedRegexp = /Minified React error #\d+;/i;

function getPopSize(ex) {
  if (ex) {
    if (typeof ex.framesToPop === 'number') {
      return ex.framesToPop;
    }

    if (reactMinifiedRegexp.test(ex.message)) {
      return 1;
    }
  }

  return 0;
}
/**
 * There are cases where stacktrace.message is an Event object
 * https://github.com/getsentry/sentry-javascript/issues/1949
 * In this specific case we try to extract stacktrace.message.error.message
 */


function extractMessage(ex) {
  var message = ex && ex.message;

  if (!message) {
    return 'No error message';
  }

  if (message.error && typeof message.error.message === 'string') {
    return message.error.message;
  }

  return message;
}
/**
 * Creates an {@link Event} from all inputs to `captureException` and non-primitive inputs to `captureMessage`.
 * @hidden
 */


function eventFromException(stackParser, exception, hint, attachStacktrace) {
  var syntheticException = hint && hint.syntheticException || undefined;
  var event = eventFromUnknownInput(stackParser, exception, syntheticException, attachStacktrace);
  addExceptionMechanism(event); // defaults to { type: 'generic', handled: true }

  event.level = 'error';

  if (hint && hint.event_id) {
    event.event_id = hint.event_id;
  }

  return resolvedSyncPromise(event);
}
/**
 * Builds and Event from a Message
 * @hidden
 */


function eventFromMessage(stackParser, message) {
  var level = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : 'info';
  var hint = arguments.length > 3 ? arguments[3] : undefined;
  var attachStacktrace = arguments.length > 4 ? arguments[4] : undefined;
  var syntheticException = hint && hint.syntheticException || undefined;
  var event = eventFromString(stackParser, message, syntheticException, attachStacktrace);
  event.level = level;

  if (hint && hint.event_id) {
    event.event_id = hint.event_id;
  }

  return resolvedSyncPromise(event);
}
/**
 * @hidden
 */


function eventFromUnknownInput(stackParser, exception, syntheticException, attachStacktrace, isUnhandledRejection) {
  var event;

  if (isErrorEvent(exception) && exception.error) {
    // If it is an ErrorEvent with `error` property, extract it to get actual Error
    var errorEvent = exception;
    return eventFromError(stackParser, errorEvent.error);
  } // If it is a `DOMError` (which is a legacy API, but still supported in some browsers) then we just extract the name
  // and message, as it doesn't provide anything else. According to the spec, all `DOMExceptions` should also be
  // `Error`s, but that's not the case in IE11, so in that case we treat it the same as we do a `DOMError`.
  //
  // https://developer.mozilla.org/en-US/docs/Web/API/DOMError
  // https://developer.mozilla.org/en-US/docs/Web/API/DOMException
  // https://webidl.spec.whatwg.org/#es-DOMException-specialness


  if (isDOMError(exception) || isDOMException(exception)) {
    var domException = exception;

    if ('stack' in exception) {
      event = eventFromError(stackParser, exception);
    } else {
      var name = domException.name || (isDOMError(domException) ? 'DOMError' : 'DOMException');
      var message = domException.message ? "".concat(name, ": ").concat(domException.message) : name;
      event = eventFromString(stackParser, message, syntheticException, attachStacktrace);
      addExceptionTypeValue(event, message);
    }

    if ('code' in domException) {
      event.tags = _objectSpread(_objectSpread({}, event.tags), {}, {
        'DOMException.code': "".concat(domException.code)
      });
    }

    return event;
  }

  if (isError(exception)) {
    // we have a real Error object, do nothing
    return eventFromError(stackParser, exception);
  }

  if (isPlainObject(exception) || isEvent(exception)) {
    // If it's a plain object or an instance of `Event` (the built-in JS kind, not this SDK's `Event` type), serialize
    // it manually. This will allow us to group events based on top-level keys which is much better than creating a new
    // group on any key/value change.
    var objectException = exception;
    event = eventFromPlainObject(stackParser, objectException, syntheticException, isUnhandledRejection);
    addExceptionMechanism(event, {
      synthetic: true
    });
    return event;
  } // If none of previous checks were valid, then it means that it's not:
  // - an instance of DOMError
  // - an instance of DOMException
  // - an instance of Event
  // - an instance of Error
  // - a valid ErrorEvent (one with an error property)
  // - a plain Object
  //
  // So bail out and capture it as a simple message:


  event = eventFromString(stackParser, exception, syntheticException, attachStacktrace);
  addExceptionTypeValue(event, "".concat(exception), undefined);
  addExceptionMechanism(event, {
    synthetic: true
  });
  return event;
}
/**
 * @hidden
 */


function eventFromString(stackParser, input, syntheticException, attachStacktrace) {
  var event = {
    message: input
  };

  if (attachStacktrace && syntheticException) {
    var frames = parseStackFrames(stackParser, syntheticException);

    if (frames.length) {
      event.exception = {
        values: [{
          value: input,
          stacktrace: {
            frames: frames
          }
        }]
      };
    }
  }

  return event;
}

export { eventFromError, eventFromException, eventFromMessage, eventFromPlainObject, eventFromString, eventFromUnknownInput, exceptionFromError, parseStackFrames };